import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../components/AuthProvider';
import { puedeEditar } from '../../domain/permissions';
import {
  actualizarBloque,
  actualizarCurso,
  actualizarLeccion,
  actualizarOpcion,
  actualizarPregunta,
  borrarBloque,
  borrarLeccion,
  borrarOpcion,
  borrarPregunta,
  crearBloque,
  crearLeccion,
  crearOpcion,
  crearPregunta,
  listarBloques,
  listarLecciones,
  listarOpcionesEditables,
  listarPreguntas,
  marcarOpcionCorrecta,
  obtenerCurso,
  type BloqueRow,
  type CursoRow,
  type LeccionRow,
  type OpcionEditable,
  type PreguntaRow,
} from '../../api/academia';

/**
 * Tipos de bloque que el editor sabe crear hoy.
 *
 * `presentacion` no está: su contenido son SVG de varios kilobytes escritos a mano, y un
 * campo de texto no es la herramienta para eso. Los que ya existen se pueden reordenar y
 * borrar, pero no editar acá — se ven marcados como tal.
 *
 * `documento` y `video_archivo` esperan a que exista dónde subir archivos (fase 2).
 */
const TIPOS_EDITABLES = [
  { tipo: 'texto', etiqueta: 'Texto', campo: 'html', ayuda: 'Acepta HTML: <p>, <b>, <ul>, <li>.' },
  {
    tipo: 'video_youtube',
    etiqueta: 'Video de YouTube',
    campo: 'videoId',
    ayuda: 'Solo el id del video: en youtube.com/watch?v=ABC123, es ABC123.',
  },
  {
    tipo: 'imagen',
    etiqueta: 'Imagen',
    campo: 'storagePath',
    ayuda: 'Ruta de la imagen, por ejemplo /productos/pan.jpg',
  },
] as const;

export default function AcademiaCurso() {
  const { cursoId } = useParams<{ cursoId: string }>();
  const { roles } = useAuth();
  const editable = puedeEditar('academia', roles);

  const [curso, setCurso] = useState<CursoRow | null>(null);
  const [lecciones, setLecciones] = useState<LeccionRow[]>([]);
  const [preguntas, setPreguntas] = useState<PreguntaRow[]>([]);
  const [opciones, setOpciones] = useState<OpcionEditable[]>([]);
  const [bloques, setBloques] = useState<Map<string, BloqueRow[]>>(new Map());
  const [abierta, setAbierta] = useState<string | null>(null);
  const [cargando, setCargando] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [aviso, setAviso] = useState('');

  const recargar = useCallback(async () => {
    if (!cursoId) return;
    try {
      const [c, l, p, o] = await Promise.all([
        obtenerCurso(cursoId),
        listarLecciones(cursoId),
        listarPreguntas(cursoId),
        listarOpcionesEditables(cursoId),
      ]);
      setCurso(c);
      setLecciones(l);
      setPreguntas(p);
      setOpciones(o);
    } catch (err) {
      setError(mensaje(err));
    } finally {
      setCargando(false);
    }
  }, [cursoId]);

  useEffect(() => {
    void recargar();
  }, [recargar]);

  async function abrirLeccion(id: string) {
    if (abierta === id) {
      setAbierta(null);
      return;
    }
    setAbierta(id);
    if (!bloques.has(id)) {
      try {
        const b = await listarBloques(id);
        setBloques((m) => new Map(m).set(id, b));
      } catch (err) {
        setError(mensaje(err));
      }
    }
  }

  async function recargarBloques(leccionId: string) {
    const frescos = await listarBloques(leccionId);
    setBloques((m) => new Map(m).set(leccionId, frescos));
  }

  /** Envuelve cada acción: un error de la API tiene que verse, no morir en la consola. */
  async function accion(fn: () => Promise<void>, textoAviso = 'Guardado') {
    setError(null);
    try {
      await fn();
      setAviso(textoAviso);
      setTimeout(() => setAviso(''), 2000);
    } catch (err) {
      setError(mensaje(err));
    }
  }

  if (cargando) return <div className="p-8 text-center text-gray-500">Cargando…</div>;
  if (!curso) {
    return (
      <div className="p-8 text-center">
        <p className="font-semibold">No encontramos ese curso</p>
        <Link to="/panel/academia" className="mt-3 inline-block text-sm text-blue-700 underline">
          Volver a Academia
        </Link>
      </div>
    );
  }

  const sinCorrecta = preguntas.filter(
    (p) => !opciones.some((o) => o.pregunta_id === p.id && o.es_correcta),
  );

  return (
    <div className="p-4 sm:p-6">
      <Link to="/panel/academia" className="text-sm text-blue-700">
        ← Academia
      </Link>

      <header className="mb-5 mt-2 flex flex-wrap items-center gap-3">
        <span className="text-3xl">{curso.emoji}</span>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{curso.titulo}</h1>
          <p className="text-sm text-gray-500">
            {lecciones.length} lecciones · {preguntas.length} preguntas · estado{' '}
            <b>{curso.estado}</b>
          </p>
        </div>
        {aviso && (
          <span className="rounded-full bg-green-100 px-3 py-1 text-xs font-bold text-green-800">
            {aviso}
          </span>
        )}
      </header>

      {error && (
        <p className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700" role="alert">
          {error}
        </p>
      )}

      {curso.estado === 'publicado' && sinCorrecta.length > 0 && (
        <p className="mb-4 rounded-lg bg-amber-50 px-4 py-3 text-sm text-amber-900">
          ⚠️ Este curso está <b>publicado</b> y tiene {sinCorrecta.length} pregunta
          {sinCorrecta.length === 1 ? '' : 's'} sin alternativa correcta marcada. Quien lo rinda
          nunca podrá acertarla.
        </p>
      )}

      {/* ---------- ficha ---------- */}
      <section className="mb-6 rounded-lg border bg-white p-4">
        <h2 className="mb-3 font-bold">Ficha del curso</h2>
        <div className="grid gap-3 sm:grid-cols-2">
          <Campo
            etiqueta="Título"
            valor={curso.titulo}
            editable={editable}
            onGuardar={(v) =>
              accion(async () => {
                await actualizarCurso(curso.id, { titulo: v });
                await recargar();
              })
            }
          />
          <Campo
            etiqueta="Emoji"
            valor={curso.emoji}
            editable={editable}
            onGuardar={(v) =>
              accion(async () => {
                await actualizarCurso(curso.id, { emoji: v });
                await recargar();
              })
            }
          />
          <Campo
            etiqueta="Descripción"
            valor={curso.descripcion}
            editable={editable}
            multilinea
            onGuardar={(v) =>
              accion(async () => {
                await actualizarCurso(curso.id, { descripcion: v });
                await recargar();
              })
            }
          />
          <div className="grid grid-cols-2 gap-3">
            <Campo
              etiqueta="Nota mínima"
              valor={String(curso.nota_minima)}
              editable={editable}
              onGuardar={(v) =>
                accion(async () => {
                  const n = Number(v);
                  if (!Number.isInteger(n) || n < 0 || n > 100) {
                    throw new Error('La nota mínima va de 0 a 100.');
                  }
                  await actualizarCurso(curso.id, { nota_minima: n });
                  await recargar();
                })
              }
            />
            <Campo
              etiqueta="Duración (min)"
              valor={String(curso.duracion_min)}
              editable={editable}
              onGuardar={(v) =>
                accion(async () => {
                  const n = Number(v);
                  if (!Number.isInteger(n) || n <= 0) throw new Error('La duración debe ser un número de minutos.');
                  await actualizarCurso(curso.id, { duracion_min: n });
                  await recargar();
                })
              }
            />
          </div>
        </div>
      </section>

      {/* ---------- lecciones ---------- */}
      <section className="mb-6 rounded-lg border bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-bold">Lecciones</h2>
          {editable && (
            <button
              onClick={() =>
                accion(async () => {
                  await crearLeccion(curso.id, 'Lección nueva', lecciones.length + 1);
                  await recargar();
                }, 'Lección creada')
              }
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              + Lección
            </button>
          )}
        </div>

        {lecciones.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            Este curso todavía no tiene lecciones.
          </p>
        ) : (
          <div className="space-y-2">
            {lecciones.map((l) => (
              <div key={l.id} className="rounded-lg border">
                <div className="flex flex-wrap items-center gap-2 p-3">
                  <span className="text-xs font-bold text-gray-400">{l.orden}</span>
                  <div className="min-w-[180px] flex-1">
                    <Campo
                      etiqueta=""
                      valor={l.titulo}
                      editable={editable}
                      onGuardar={(v) =>
                        accion(async () => {
                          await actualizarLeccion(l.id, { titulo: v });
                          await recargar();
                        })
                      }
                    />
                  </div>
                  <button
                    onClick={() => void abrirLeccion(l.id)}
                    className="rounded-lg border px-3 py-1.5 text-xs font-semibold"
                  >
                    {abierta === l.id ? 'Cerrar' : 'Bloques'}
                  </button>
                  {editable && (
                    <button
                      onClick={() =>
                        accion(async () => {
                          if (!confirm(`¿Borrar la lección «${l.titulo}» y todos sus bloques?`)) return;
                          await borrarLeccion(l.id);
                          await recargar();
                        }, 'Lección borrada')
                      }
                      className="rounded-lg border border-red-200 px-3 py-1.5 text-xs font-semibold text-red-700"
                    >
                      Borrar
                    </button>
                  )}
                </div>

                {abierta === l.id && (
                  <BloquesDeLeccion
                    leccion={l}
                    bloques={bloques.get(l.id) ?? []}
                    editable={editable}
                    onAccion={accion}
                    onRecargar={() => recargarBloques(l.id)}
                  />
                )}
              </div>
            ))}
          </div>
        )}
      </section>

      {/* ---------- quiz ---------- */}
      <section className="rounded-lg border bg-white p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-bold">Quiz</h2>
          {editable && (
            <button
              onClick={() =>
                accion(async () => {
                  const id = await crearPregunta(curso.id, 'Pregunta nueva', preguntas.length + 1);
                  // Dos alternativas de arranque: una pregunta con una sola no es una pregunta.
                  await crearOpcion(id, 'Alternativa 1', 0);
                  await crearOpcion(id, 'Alternativa 2', 1);
                  await recargar();
                }, 'Pregunta creada')
              }
              className="rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-semibold text-white"
            >
              + Pregunta
            </button>
          )}
        </div>

        {preguntas.length === 0 ? (
          <p className="py-4 text-center text-sm text-gray-500">
            Este curso todavía no tiene preguntas.
          </p>
        ) : (
          <div className="space-y-4">
            {preguntas.map((p) => {
              const suyas = opciones
                .filter((o) => o.pregunta_id === p.id)
                .sort((a, b) => a.orden - b.orden);
              const tieneCorrecta = suyas.some((o) => o.es_correcta);
              return (
                <div key={p.id} className="rounded-lg border p-3">
                  <div className="mb-2 flex items-start gap-2">
                    <span className="mt-2 text-xs font-bold text-gray-400">{p.orden}</span>
                    <div className="flex-1">
                      <Campo
                        etiqueta=""
                        valor={p.enunciado}
                        editable={editable}
                        multilinea
                        onGuardar={(v) =>
                          accion(async () => {
                            await actualizarPregunta(p.id, { enunciado: v });
                            await recargar();
                          })
                        }
                      />
                    </div>
                    {editable && (
                      <button
                        onClick={() =>
                          accion(async () => {
                            if (!confirm('¿Borrar esta pregunta y sus alternativas?')) return;
                            await borrarPregunta(p.id);
                            await recargar();
                          }, 'Pregunta borrada')
                        }
                        className="rounded-lg border border-red-200 px-2 py-1 text-xs text-red-700"
                      >
                        ✕
                      </button>
                    )}
                  </div>

                  {!tieneCorrecta && (
                    <p className="mb-2 text-xs font-semibold text-amber-700">
                      ⚠️ Ninguna alternativa está marcada como correcta.
                    </p>
                  )}

                  <ul className="space-y-1.5">
                    {suyas.map((o) => (
                      <li key={o.id} className="flex items-center gap-2">
                        <input
                          type="radio"
                          name={`correcta-${p.id}`}
                          checked={o.es_correcta}
                          disabled={!editable}
                          onChange={() =>
                            accion(async () => {
                              await marcarOpcionCorrecta(o.id);
                              await recargar();
                            }, 'Respuesta correcta marcada')
                          }
                          aria-label={`Marcar «${o.texto}» como correcta`}
                        />
                        <div className="flex-1">
                          <Campo
                            etiqueta=""
                            valor={o.texto}
                            editable={editable}
                            onGuardar={(v) =>
                              accion(async () => {
                                await actualizarOpcion(o.id, { texto: v });
                                await recargar();
                              })
                            }
                          />
                        </div>
                        {editable && suyas.length > 2 && (
                          <button
                            onClick={() =>
                              accion(async () => {
                                await borrarOpcion(o.id);
                                await recargar();
                              }, 'Alternativa borrada')
                            }
                            className="text-xs text-red-700"
                          >
                            ✕
                          </button>
                        )}
                      </li>
                    ))}
                  </ul>

                  {editable && (
                    <button
                      onClick={() =>
                        accion(async () => {
                          await crearOpcion(p.id, 'Alternativa nueva', suyas.length);
                          await recargar();
                        }, 'Alternativa creada')
                      }
                      className="mt-2 text-xs font-semibold text-blue-700"
                    >
                      + Alternativa
                    </button>
                  )}

                  <div className="mt-3">
                    <p className="mb-1 text-xs font-semibold text-gray-500">
                      Explicación (se muestra al responder)
                    </p>
                    <Campo
                      etiqueta=""
                      valor={p.explicacion}
                      editable={editable}
                      multilinea
                      onGuardar={(v) =>
                        accion(async () => {
                          await actualizarPregunta(p.id, { explicacion: v });
                          await recargar();
                        })
                      }
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>
    </div>
  );
}

// ---------------------------------------------------------------------------

function BloquesDeLeccion({
  leccion,
  bloques,
  editable,
  onAccion,
  onRecargar,
}: {
  leccion: LeccionRow;
  bloques: BloqueRow[];
  editable: boolean;
  onAccion: (fn: () => Promise<void>, aviso?: string) => Promise<void>;
  onRecargar: () => Promise<void>;
}) {
  return (
    <div className="border-t bg-gray-50 p-3">
      {bloques.length === 0 && (
        <p className="py-2 text-center text-sm text-gray-500">Esta lección no tiene bloques.</p>
      )}

      <div className="space-y-2">
        {bloques.map((b) => {
          const editableAqui = TIPOS_EDITABLES.find((t) => t.tipo === b.tipo);
          return (
            <div key={b.id} className="rounded-lg border bg-white p-3">
              <div className="mb-1.5 flex items-center justify-between">
                <span className="text-xs font-bold uppercase tracking-wide text-gray-500">
                  {b.orden} · {editableAqui?.etiqueta ?? b.tipo}
                </span>
                {editable && (
                  <button
                    onClick={() =>
                      onAccion(async () => {
                        if (!confirm('¿Borrar este bloque?')) return;
                        await borrarBloque(b.id);
                        await onRecargar();
                      }, 'Bloque borrado')
                    }
                    className="text-xs text-red-700"
                  >
                    ✕
                  </button>
                )}
              </div>

              {editableAqui ? (
                <Campo
                  etiqueta=""
                  valor={String(b.contenido[editableAqui.campo] ?? '')}
                  editable={editable}
                  multilinea={b.tipo === 'texto'}
                  onGuardar={(v) =>
                    onAccion(async () => {
                      await actualizarBloque(b.id, { contenido: { [editableAqui.campo]: v } });
                      await onRecargar();
                    })
                  }
                />
              ) : (
                // `presentacion` son SVG de varios kilobytes; `documento` y `video_archivo`
                // esperan a que exista dónde subir archivos. Se pueden borrar, no editar acá.
                <p className="rounded border border-dashed px-3 py-2 text-xs text-gray-500">
                  Este tipo de bloque no se edita desde acá todavía. Se puede reordenar o borrar.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {editable && (
        <div className="mt-2 flex flex-wrap gap-2">
          {TIPOS_EDITABLES.map((t) => (
            <button
              key={t.tipo}
              title={t.ayuda}
              onClick={() =>
                onAccion(async () => {
                  await crearBloque(leccion.id, t.tipo, { [t.campo]: '' }, bloques.length + 1);
                  await onRecargar();
                }, 'Bloque creado')
              }
              className="rounded-lg border bg-white px-3 py-1.5 text-xs font-semibold"
            >
              + {t.etiqueta}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Campo que se guarda al salir del foco, no con un botón por campo.
 *
 * Con veinte campos en pantalla, veinte botones «Guardar» es más ruido que ayuda. Solo
 * llama al servidor si el valor CAMBIÓ: sin esa comprobación, pasar por los campos con el
 * tabulador dispararía una escritura por cada uno.
 */
function Campo({
  etiqueta,
  valor,
  editable,
  multilinea,
  onGuardar,
}: {
  etiqueta: string;
  valor: string;
  editable: boolean;
  multilinea?: boolean;
  onGuardar: (v: string) => Promise<void> | void;
}) {
  const [borrador, setBorrador] = useState(valor);
  useEffect(() => setBorrador(valor), [valor]);

  const comun = {
    value: borrador,
    disabled: !editable,
    onChange: (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) =>
      setBorrador(e.target.value),
    onBlur: () => {
      if (borrador !== valor) void onGuardar(borrador);
    },
    className:
      'w-full rounded-lg border px-3 py-2 text-sm disabled:bg-gray-50 disabled:text-gray-500',
  };

  return (
    <label className="block">
      {etiqueta && (
        <span className="mb-1 block text-xs font-semibold text-gray-500">{etiqueta}</span>
      )}
      {multilinea ? <textarea rows={3} {...comun} /> : <input {...comun} />}
    </label>
  );
}

function mensaje(err: unknown): string {
  const m = err instanceof Error ? err.message : String(err);
  if (m.includes('PGRST106')) {
    return 'El esquema `academia` no está expuesto en la API del proyecto (Settings → API → Exposed schemas).';
  }
  if (m.includes('42501')) return 'No tienes permiso para esta acción.';
  return m;
}
